import React from "react";
import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  FIGMA_EDIT_BUTTONS,
  FIGMA_EDIT_ERROR,
  FIGMA_EDIT_LOOKUP_ERROR,
  FigmaEditCopyInput,
  getFigmaEditCopy,
} from "./figmaEditCopy";

// Mirrors DesignGalleryAddressBoxModal so the warnings in the design step read as one system.
const useStyles = makeStyles({
  dialog: {
    "& .MuiDialogContent-root, .MuiDialogTitle-root": {
      padding: 0,
    },
    "& .MuiDialog-paper": {
      borderRadius: "8px",
    },
  },
  content: {
    padding: "32px 24px 24px 24px",
    maxWidth: "600px",
  },
  title: {
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    marginBottom: "16px",
    "& > h2": {
      fontWeight: "700",
      fontSize: "22px",
      lineHeight: "26px",
      fontFamily: `"Inter", "Inter", "sans-serif"`,
      color: "#000",
    },
  },
  body: {
    textAlign: "center" as const,
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "22px",
    fontFamily: `"Inter", "Inter", "sans-serif"`,
    margin: 0,
    color: "#555",
  },
  error: {
    textAlign: "center" as const,
    fontSize: "14px",
    lineHeight: "20px",
    fontFamily: `"Inter", "Inter", "sans-serif"`,
    color: "#D32F2F",
    marginTop: "16px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    width: "100%",
    marginTop: "32px",
  },
  primaryButton: {
    background: "#EE4360",
    border: "1px solid #EE4360",
    borderRadius: "8px",
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    padding: "0 24px",
    boxSizing: "border-box" as const,
    minWidth: "150px",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "20px",
    textTransform: "initial" as const,
    "&:hover": {
      background: "#FFFFFF",
      border: "1px solid #EE4360",
      color: "#EE4360",
    },
    "&.Mui-disabled": {
      color: "#FFFFFF",
      opacity: 0.7,
    },
  },
  secondaryButton: {
    borderRadius: "8px",
    border: "1px solid #000",
    height: "40px",
    minHeight: "40px",
    maxHeight: "40px",
    padding: "0 24px",
    boxSizing: "border-box" as const,
    minWidth: "150px",
    fontWeight: "bold" as const,
    fontSize: "16px",
    lineHeight: "20px",
    textTransform: "initial" as const,
    background: "#FFFFFF",
    color: "#000",
    "&:hover": {
      background: "#F5F5F5",
    },
    "&.Mui-disabled": {
      opacity: 0.6,
    },
  },
  spinner: {
    color: "inherit",
    marginRight: "8px",
  },
});

export type FigmaEditChoice = "copy" | "flatten";

interface FigmaEditDialogProps extends FigmaEditCopyInput {
  open: boolean;
  // The choice whose request is in flight, if any.
  busyChoice?: FigmaEditChoice | null;
  showError?: boolean;
  onClose: () => void;
  onEditCopy: () => void;
  onFlattenOriginal: () => void;
}

export const FigmaEditDialog: React.FC<FigmaEditDialogProps> = ({
  open,
  hasPlaceholders,
  usedByCampaign,
  busyChoice = null,
  showError = false,
  onClose,
  onEditCopy,
  onFlattenOriginal,
}) => {
  const classes = useStyles();
  const copy = getFigmaEditCopy({ hasPlaceholders, usedByCampaign });
  const isBusy = busyChoice !== null;
  const spinner = <CircularProgress size={16} className={classes.spinner} />;

  return (
    <Dialog
      open={open}
      // A dismissal changes nothing; it is ignored only while a request is in flight.
      onClose={() => {
        if (!isBusy) onClose();
      }}
      aria-labelledby="figma-edit-dialog-title"
      className={classes.dialog}
      data-cypress-id={"figmaEditDialog"}
    >
      <Box className={classes.content} data-cypress-id={"figmaEditDialogContentWrapper"}>
        <DialogContent data-cypress-id={"figmaEditDialogContent"}>
          <DialogTitle id="figma-edit-dialog-title" className={classes.title} data-cypress-id={"figmaEditDialogTitle"}>
            {copy.title}
          </DialogTitle>
          <DialogContentText className={classes.body} data-cypress-id={"figmaEditDialogBody"}>
            {copy.body}
          </DialogContentText>
          {showError && (
            <div role="alert" className={classes.error} data-cypress-id={"figmaEditDialogError"}>
              {FIGMA_EDIT_ERROR}
            </div>
          )}
        </DialogContent>
        <Box className={classes.buttonContainer} data-cypress-id={"figmaEditDialogButtonContainer"}>
          <Button
            className={classes.secondaryButton}
            onClick={copy.secondaryAction === "flatten" ? onFlattenOriginal : onClose}
            disabled={isBusy}
            data-cypress-id={"figmaEditDialogSecondaryButton"}
          >
            {busyChoice === "flatten" && spinner}
            {copy.secondaryLabel}
          </Button>
          <Button
            className={classes.primaryButton}
            onClick={onEditCopy}
            disabled={isBusy}
            data-cypress-id={"figmaEditDialogPrimaryButton"}
          >
            {busyChoice === "copy" && spinner}
            {copy.primaryLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

interface FigmaEditLookupErrorDialogProps {
  open: boolean;
  onClose: () => void;
  onRetry: () => void;
}

// Shown by the builder gate when it cannot tell whether a design came from Figma, instead of opening it unchecked.
export const FigmaEditLookupErrorDialog: React.FC<FigmaEditLookupErrorDialogProps> = ({ open, onClose, onRetry }) => {
  const classes = useStyles();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="figma-edit-lookup-error-title"
      className={classes.dialog}
      data-cypress-id={"figmaEditLookupErrorDialog"}
    >
      <Box className={classes.content}>
        <DialogContent>
          <DialogTitle
            id="figma-edit-lookup-error-title"
            className={classes.title}
            data-cypress-id={"figmaEditLookupErrorDialogTitle"}
          >
            {FIGMA_EDIT_LOOKUP_ERROR.title}
          </DialogTitle>
          <DialogContentText className={classes.body} data-cypress-id={"figmaEditLookupErrorDialogBody"}>
            {FIGMA_EDIT_LOOKUP_ERROR.body}
          </DialogContentText>
        </DialogContent>
        <Box className={classes.buttonContainer}>
          <Button
            className={classes.secondaryButton}
            onClick={onClose}
            data-cypress-id={"figmaEditLookupErrorDialogCancelButton"}
          >
            {FIGMA_EDIT_BUTTONS.cancel}
          </Button>
          <Button
            className={classes.primaryButton}
            onClick={onRetry}
            data-cypress-id={"figmaEditLookupErrorDialogRetryButton"}
          >
            {FIGMA_EDIT_LOOKUP_ERROR.retry}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
